import { Component } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import { notAllNumbersValidator, SaveForm } from '@tokeet-frontend/tv3-platform'
import { defaultTaskColors, getTaskStatusOptions, selectTaskLists, UpdateTaskPayload } from '@tokeet-frontend/tasks'
import * as moment from 'moment'
import { map, startWith, switchMap } from 'rxjs/operators'

@Component({
  selector: 'app-bulk-edit-tasks-dialog',
  templateUrl: './bulk-edit-tasks-dialog.component.html',
  host: { class: 'modal-content' },
})
export class BulkEditTasksDialogComponent {
  readonly colors = defaultTaskColors.map(({ color }) => color)
  readonly statuses = getTaskStatusOptions(false)

  form = this.fb.group({
    name: ['', [Validators.minLength(2), Validators.maxLength(100), notAllNumbersValidator]],
    list: [''],
    start: [null],
    due: [null],
    timezone: [null],
    status: [null],
    user_ids: [[]],
    rental_id: [null],
    color: [null],
    notes: [''],
  })

  private readonly taskLists$ = this.store.pipe(select(selectTaskLists))
  readonly filteredLists = this.form.get('list').valueChanges.pipe(
    startWith(''),
    switchMap((value) =>
      this.taskLists$.pipe(
        map((lists) => lists.filter((list) => list.toLowerCase().includes((value || '').toLowerCase()))),
        map((lists) => [...lists].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })))
      )
    )
  )

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    public dialogRef: MatDialogRef<BulkEditTasksDialogComponent>
  ) {}

  get hasChanges() {
    const value = this.form.getRawValue()

    return (
      !!value.name?.trim() ||
      !!value.list?.trim() ||
      !!value.start ||
      !!value.due ||
      !!value.timezone ||
      !!value.status ||
      !!value.user_ids?.length ||
      !!value.rental_id ||
      !!value.color ||
      !!value.notes?.trim()
    )
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  save(form: FormGroup) {
    const value = form.getRawValue()
    const updates: UpdateTaskPayload = {}

    if (value.name?.trim()) updates.name = value.name.trim()
    if (value.list?.trim()) updates.list = value.list.trim()
    if (value.start) updates.start = this.asTaskEpoch(value.start, value.timezone)
    if (value.due) updates.due = this.asTaskEpoch(value.due, value.timezone)
    if (value.timezone) updates.timezone = value.timezone
    if (value.status) updates.status = value.status
    if (value.user_ids?.length) updates.user_ids = value.user_ids
    if (value.rental_id) updates.rental_id = value.rental_id
    if (value.color) updates.color = value.color
    if (value.notes?.trim()) updates.notes = value.notes

    this.dialogRef.close(updates)
  }

  private asTaskEpoch(date: Date, timezone?: string) {
    return moment
      .tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        },
        timezone && moment.tz.zone(timezone) ? timezone : moment.tz.guess()
      )
      .unix()
  }
}
